<script setup lang="ts">
import { computed } from 'vue';
import { trans } from '@tools/translation';
import Icon from '@components/Icon.vue';
import { IconCaretUpDownBold } from '@icons';
import Typography from '@components/Typography.vue';
import { ViewType } from '@components/StickyTabs/map-constants';

interface Option {
  value: string,
  label: string,
}

const { view, options = [] } = defineProps<{
  view: string;
  options?: Option[];
}>();

const emit = defineEmits<{(e: 'change', payload: string): void}>();

const allOptions = computed<Option[]>(() => [
  { value: ViewType.LIST, label: trans('mobile.view.option.list') },
  { value: ViewType.MAP, label: trans('mobile.view.option.map') },
  ...options,
]);

const labelMap = computed<Map<string, string>>(
  () => new Map(allOptions.value.map((option) => [ option.value, option.label ])),
);

const currentLabel = computed<string>(() => labelMap.value.get(view) ?? '');

const onChange = (e: Event): void => {
  const target = e.target as HTMLSelectElement;
  emit('change', target.value);
};
</script>

<template>
  <label
    id="mobile-view-dropdown"
    :class="[
      'fixed inset-x-2',
      'flex items-center justify-center gap-2',
      'box-border h-12 w-fit',
      'mx-auto px-3 py-4',
      'cursor-pointer rounded-lg border border-solid border-gray bg-white shadow-[0_2px_4px_0_rgba(0,0,0,0.10)]',
      { 'bottom-[72px]': view !== ViewType.MAP },
    ]"
  >
    <Icon :svg="IconCaretUpDownBold" width="20" height="20" fill="gray-darker" />

    <Typography
      class="pointer-events-none"
      variant="body-bold"
      color="black"
    >
      {{ currentLabel }}
    </Typography>

    <select
      :class="[
        'absolute',
        'inset-0 appearance-none opacity-0',
        'cursor-pointer'
      ]"
      :value="view"
      @change="onChange"
    >
      <option
        v-for="option in allOptions"
        :key="option.value"
        :value="option.value"
      >
        {{ option.label }}
      </option>
    </select>
  </label>
</template>
